<template>
  <Component
    :is="continuous ? 'div' : BaseCard"
    no-padding
    id="candidateDetails"
    :class="!continuous && 'relative shadow-lg rounded-lg'"
  >
    <div
      :class="bannerHeight"
      class="relative flex w-full items-center justify-center overflow-hidden rounded-t-lg bg-slate-100 group group/banner">
      <img
        v-if="candidate.id || candidateBanner"
        :src="candidateBanner"
        alt="Candidate Banner"
        class="min-h-full w-auto min-w-full object-cover"/>
      <UtilityUploadImage
        show-text
        v-if="editable"
        v-model:image="candidateBanner"
        :stencil-component="RectangleStencil"
        @update:file="$emit('update:banner', $event)"
      />
      <button
        v-if="editable"
        class="pointer-events-none absolute right-2 bottom-2 z-50 flex items-center rounded-lg bg-white bg-opacity-50 group-hover/banner:bg-opacity-80 px-2 py-1 text-xs font-medium text-black group-hover/banner:underline transition-all space-x-1 hover:bg-opacity-100">
        <PencilSquareIcon
          class="h-3 w-3"/>
        <span>Change Banner</span>
      </button>
    </div>
    <div
      class="p-6">
      <div
        class="flex flex-col items-start sm:flex-row sm:justify-between">
        <UtilityPercentageProfileUpload
          :percentage="candidate.trustScore"
          :avatar="candidate.avatar"
          :name="(candidate.firstName || '') + ' ' + (candidate.lastName  || '')"
          :avatar-classes="small ? 'h-16 w-16' : 'h-24 w-24'"
          @update:avatar="$emit('update:avatar', $event)"
          :allow-upload="editable"
          avatar-styles="min-width: 5rem; min-height: 5rem;"/>
        <div
          v-if="showAutoComplete"
          class="top-20 mt-3 flex flex-row flex-wrap gap-2 right-2.5 sm:absolute sm:mt-0 sm:flex-col sm:flex-nowrap sm:items-end sm:gap-0 lg:mt-8 xl:mt-4"
          :class="small ? 'sm:pt-8' : 'sm:pt-14'">
          <FormButton
            color="gradient"
            size="md"
            flex
            class="block text-left sm:mt-2"
            @click="showPdfUploader=true">
            <div
              class="relative">
              <SparklesIcon
                class="h-4 w-4"/>
              <SparklesIcon
                class="absolute inset-0 animate-ping opacity-50"/>
            </div>
            <p
              v-if="candidate?.hasResume"
              class="ml-2 font-bold">Add New Resume</p>
            <p
              v-else
              class="ml-2 font-bold">Auto-Complete w/ Resume</p>
          </FormButton>
          <Component :is="parseResumeModal" v-model:open="showPdfUploader"/>
          <Component :is="router ? 'RouterLink' : 'a'" to="/account" href="/account" class="block">
            <FormButton color="secondary" size="md" flex class="text-left sm:mt-2" medium>
              <PencilSquareIcon class="h-4 w-4"/>
              My Contact Info
            </FormButton>
          </Component>
        </div>
        <div
          v-else-if="!editable"
          class="mt-2 flex items-center justify-end py-3"
          :class="small ? 'sm:pt-8' : 'sm:pt-14'">
          <FormButton
            type="button"
            size="sm"
            color="outline-orange"
            class="group"
            flex
            @click="showIncidentReportModal=true"
          >
            <FlagIcon
              class="my-1 -mr-2 h-4 w-4"/>
            <span
              v-if="small" class="w-0"/>
            <p
              v-else
              class="ml-0 w-0 overflow-hidden whitespace-nowrap transition-all group-hover:ml-2 group-hover:w-14">
              Report
            </p>
          </FormButton>
          <ModalIncidentReport
            v-model:open="showIncidentReportModal"
            reportable-type="Candidate"
            :reportable-id="candidate.id"
            :company-id="accountStore.user?.activeCompany?.id"
          />
          <FormButton
            v-if="candidate.userId"
            use-raw-link
            :href="`/company-messages?user=${candidate.userId}`"
            target="_blank"
            size="sm"
            color="outline-green"
            class="-mr-3 ml-2"
            flex
          >
            <ChatBubbleLeftIcon
              class="h-4 w-4 mr-1"/>
            Message
          </FormButton>
        </div>
      </div>
      <div style="min-height: 3.25rem;">
        <div
          v-if="isEditingFirstLastName" :class="showAutoComplete && 'sm:pr-64'"
          class="my-2 flex flex-col flex-wrap items-center gap-2 sm:flex-row">
          <FormInput
            :modelValue="candidate.firstName"
            @update:modelValue="handleUpdateCandidate('firstName', $event)"
            placeholder="First name"
            class="w-full sm:w-40"
          />
          <FormInput
            :modelValue="candidate.lastName"
            @update:modelValue="handleUpdateCandidate('lastName', $event)"
            placeholder="Last name"
            class="w-full sm:w-40"
          />
          <FormButton color="green" @click="toggleEdit(() => isEditingFirstLastName = false)">
            Done
          </FormButton>
        </div>
        <p
          v-else
          :class="[[editable && 'cursor-pointer hover:underline'],[continuous ? '-mt-1' : 'mt-2.5']]"
          class="font-bold group/candidate-item mt-2.5 max-w-96 truncate"
          @click="toggleEdit(() => isEditingFirstLastName = true)">
          <span
            class="text-xl">{{ candidate.firstName }} {{ candidate.lastName }}</span>
          <PencilSquareIcon
            v-if="editable"
            class="inline-block h-4 w-4 text-slate-600 opacity-50 group-hover/candidate-item:opacity-100 transition-all ml-1.5"
          />
        </p>
        <div
          v-if="isEditingCityState"
          class="mt-1 flex items-center gap-2">
          <FormCityLatLng
            :city="candidate.city"
            :state="candidate.state"
            :country="candidate.country"
            :postal-code="candidate.postalCode"
            :lat="candidate.lat"
            :lng="candidate.lng"
            @update:city="handleUpdateCandidate('city', $event)"
            @update:state="handleUpdateCandidate('state', $event)"
            @update:country="handleUpdateCandidate('country', $event)"
            @update:postalCode="handleUpdateCandidate('postalCode', $event)"
            @update:lat="handleUpdateCandidate('lat', $event)"
            @update:lng="handleUpdateCandidate('lng', $event)"
            class="w-full max-w-[16rem]"/>
          <FormButton color="green" @click="toggleEdit(() => isEditingCityState = false)">
            Done
          </FormButton>
        </div>
        <div
          v-else-if="candidate.city"
          @click="toggleEdit(() => isEditingCityState = true)"
          :class="editable && 'cursor-pointer hover:underline'"
          class="flex items-center text-sm text-slate-400 gap-1.5 group/candidate-item">
          <img
            src="https://resources.businessdraft.com/usa.svg"
            alt="Country flag"
            class="w-3.5"
          />
          <p>{{ candidate?.city }}{{ candidate?.city ? "," : "" }} {{ candidate?.state }}</p>
          <PencilSquareIcon
            v-if="editable"
            class="inline-block h-4 w-4 text-slate-600 opacity-50 group-hover/candidate-item:opacity-100 transition-all"/>
        </div>
        <button
          type="button"
          v-else-if="editable"
          @click="toggleEdit(() => isEditingCityState = true)"
          class="flex items-center text-sm font-medium text-primary-600 hover:underline">
          <img
            src="https://resources.businessdraft.com/usa.svg"
            alt="Country flag"
            class="w-3.5 mr-1.5"/>
          <PlusCircleIcon
            class="mr-1 h-4 w-4"/>
          <span>Add City & State</span>
        </button>
        <p
          v-else
          class="text-sm text-slate-400">No location set</p>
        <p
          v-if="candidate.isHeadless"
          class="mt-2 max-w-fit rounded-md bg-orange-500 px-2 py-1 font-bold text-white text-xxs">Unclaimed</p>
      </div>
    </div>
  </Component>
</template>

<script setup>
import BaseCard from "./BaseCard.vue";
import FormButton from "./FormButton.vue";
import FormCityLatLng from "./FormCityLatLng.vue";
import FormInput from "./FormInput.vue";
import ModalIncidentReport from "./ModalIncidentReport.vue";
import UtilityPercentageProfileUpload from "./UtilityPercentageProfileUpload.vue";
import UtilityUploadImage from "./UtilityUploadImage.vue";
import {computed, defineAsyncComponent, inject, onMounted, ref, watch} from "vue";
import {RectangleStencil} from "vue-advanced-cropper";
import {FlagIcon, PencilSquareIcon, PlusCircleIcon, SparklesIcon} from "@heroicons/vue/24/solid/index.js";
import {useAccountStore} from "../stores/AccountStore.js";
import {useRouter} from "vue-router";
import {ChatBubbleLeftIcon} from "@heroicons/vue/24/solid";

const emits = defineEmits(["update:banner", "update:avatar", "update:candidate"]);
const props = defineProps({
  candidate: Object,
  continuous: Boolean,
  small: Boolean,
  editable: Boolean,
  showAutoComplete: Boolean,
});

const accountStore = useAccountStore();
const hasRouter = inject("hasRouter", true);
const router = hasRouter ? useRouter() : null;

const candidateBanner = ref(props.candidate.banner || "https://resources.businessdraft.com/default-banner.png");
const showIncidentReportModal = ref(false);
const isEditingFirstLastName = ref(false);
const isEditingCityState = ref(false);
const showPdfUploader = ref(false);

const parseResumeModal = defineAsyncComponent(() => import("./ParseResumeModal.vue"));

const bannerHeight = computed(() => {
  return props.small ? "h-28 lg:h-32 xl:h-28 -mb-14" : "h-32 lg:h-40 xl:h-36 -mb-20";
});

const handleUpdateCandidate = (key, value) => {
  if (!props.editable) return;

  const candidate = {...props.candidate};
  candidate[key] = value;
  emits("update:candidate", candidate);
};

const toggleEdit = (callback) => {
  if (!props.editable) return;
  callback();
};

watch(() => props.candidate.banner, (banner) => {
  candidateBanner.value = banner || "https://resources.businessdraft.com/default-banner.png";
}, {immediate: true});

onMounted(() => {
  if (!router) return;

  const showResumeParserQuery = router.currentRoute.value.query.auto;
  if (!showResumeParserQuery || !props.showAutoComplete) return;
  showPdfUploader.value = true;
});
</script>
